import { useState } from 'react';
import { Key, Webhook, Plug, Filter, Download } from 'lucide-react';

const AuditLogs: React.FC = () => {
  const [filter, setFilter] = useState('all');
  const [dateRange, setDateRange] = useState('30');

  const webhooks = [
    { id: '1', name: 'File Analysis Webhook', url: 'https://api.example.com/webhook', lastTriggered: '12:35 PM', status: 'active' },
    { id: '2', name: 'Storage Update Webhook', url: 'https://api.example.com/storage', lastTriggered: '11:20 AM', status: 'active' },
    { id: '3', name: 'System', url: 'Internal', lastTriggered: '9:30 AM', status: 'active' },
  ];

  const auditLogs = [
    { id: '1', action: 'File Analyzed', user: 'John Doe', timestamp: '2026-01-15 14:30', details: 'mountain_landscape.jpg' },
    { id: '2', action: 'API Key Generated', user: 'Jane Smith', timestamp: '2026-01-15 12:15', details: 'New secret key created' },
    { id: '3', action: 'Team Member Added', user: 'John Doe', timestamp: '2026-01-15 10:00', details: 'mark@acme.com invited as Editor' },
    { id: '4', action: 'Plan Upgraded', user: 'John Doe', timestamp: '2026-01-14 16:45', details: 'Upgraded to Business Pro' },
  ];

  return (
    <div className="p-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Audit Logs</h1>
        <p className="text-gray-600 mb-8">Monitor API activity and system events</p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* API Keys */}
          <div className="lg:col-span-2 space-y-6">
            <div className="card">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Key className="w-5 h-5" />
                  API Keys
                </h2>
              </div>
              
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-900">Public API Key</span>
                    <button className="text-sm text-primary-600 hover:text-primary-700">Generate New Key</button>
                  </div>
                  <code className="text-sm text-gray-600 bg-white px-3 py-2 rounded border border-gray-200 block">
                    pk_live_51HxYz2KqZ9X8Yw3N4M5...
                  </code>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-900">Secret API Key</span>
                    <button className="text-sm text-primary-600 hover:text-primary-700">Generate New Key</button>
                  </div>
                  <code className="text-sm text-gray-600 bg-white px-3 py-2 rounded border border-gray-200 block">
                    sk_live_51HxYz2KqZ9X8Yw3N4M5...
                  </code>
                </div>
              </div>
            </div>

            {/* Audit Log Table */}
            <div className="card">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
                <div className="flex items-center gap-3">
                  <select
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="input-field text-sm py-1"
                  >
                    <option value="all">All Actions</option>
                    <option value="file">File Operations</option>
                    <option value="api">API Keys</option>
                    <option value="team">Team</option>
                  </select>
                  <select
                    value={dateRange}
                    onChange={(e) => setDateRange(e.target.value)}
                    className="input-field text-sm py-1"
                  >
                    <option value="7">Last 7 Days</option>
                    <option value="30">Last 30 Days</option>
                    <option value="90">Last 90 Days</option>
                  </select>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Action</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">User</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Timestamp</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Details</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {auditLogs.map((log) => (
                      <tr key={log.id}>
                        <td className="py-3 px-4 text-sm font-medium text-gray-900">{log.action}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">{log.user}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">{log.timestamp}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">{log.details}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Webhooks */}
            <div className="card">
              <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
                <Webhook className="w-5 h-5" />
                Webhooks
              </h2>
              <div className="space-y-4">
                {webhooks.map((webhook) => (
                  <div key={webhook.id} className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-900">{webhook.name}</span>
                      <span className="text-xs text-gray-500">{webhook.lastTriggered}</span>
                    </div>
                    <p className="text-xs text-gray-600 truncate">{webhook.url}</p>
                  </div>
                ))}
              </div>
              <button className="w-full mt-4 btn-secondary text-sm">
                Configure Webhooks
              </button>
            </div>

            {/* Integration Options */}
            <div className="card">
              <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
                <Plug className="w-5 h-5" />
                Integration Options
              </h2>
              <div className="grid grid-cols-2 gap-3">
                <button className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center mx-auto mb-2">
                    <Download className="w-4 h-4 text-blue-600" />
                  </div>
                  <span className="text-xs font-medium text-gray-700">Slack</span>
                </button>
                <button className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="w-8 h-8 bg-green-100 rounded flex items-center justify-center mx-auto mb-2">
                    <Download className="w-4 h-4 text-green-600" />
                  </div>
                  <span className="text-xs font-medium text-gray-700">Zapier</span>
                </button>
                <button className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="w-8 h-8 bg-purple-100 rounded flex items-center justify-center mx-auto mb-2">
                    <Download className="w-4 h-4 text-purple-600" />
                  </div>
                  <span className="text-xs font-medium text-gray-700">Webhook</span>
                </button>
                <button className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="w-8 h-8 bg-yellow-100 rounded flex items-center justify-center mx-auto mb-2">
                    <Download className="w-4 h-4 text-yellow-600" />
                  </div>
                  <span className="text-xs font-medium text-gray-700">API</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditLogs;
